import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CameraOutlined,
  CarOutlined,
  CheckOutlined,
  CloseOutlined,
  RightOutlined,
  HeartOutlined,
  HomeOutlined,
  MinusOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import GooglePlacesAutocompleteInput from "common/GooglePlacesAutocompleteInput";
import { useFileUpload } from "hooks/useFileUpload";
import useAirports from "hooks/airports/useAirports";
import useMeetingPoints from "hooks/airline/useMeetingPoints";
import useTravelTaskCategories from "hooks/airline/useTravelTaskCategories";
import useTaskCategories from "hooks/tasks/useTaskCategories";
import { createTaskRequest } from "services/olitaskTaskService";
import apiClient from "utils/apiClient";
import { bearerGuestToken, ensureGuestSession } from "utils/guestSession";
import { addressFieldsFromPlaceDetails } from "utils/googlePlaces";
import paths from "routes/paths";
import "./TaskCreatePage.css";

const { Paragraph, Text, Title } = Typography;

const LOCATION_TIMEOUT_MS = 10000;
const TRAVO_TASK_CATEGORY_IDS = ["55fd111a-a160-413b-ab7a-256dad0e5b09"];

const googleMapsKeyFromConfigResponse = (response) => {
  const config = response?.data?.data || response?.data || {};
  return (
    config.googleMapsApiKey ||
    config.googleMapsApiMatrixKey ||
    config.googleMapsApiBrowserKey ||
    config.googleMapsKey ||
    config.mapsApiKey ||
    ""
  );
};

const COLORS = {
  bg: "#F6FBF9",
  card: "#FFFFFF",
  text: "#0F172A",
  muted: "#475569",
  green: "#27ae30",
  greenDark: "#0e5604",
  greenSoft: "#E7F6EF",
  blue: "#2385DB",
  blueDark: "#090359",
  blueSoft: "#EAF4FF",
  line: "#EEF2EE",
  red: "#cf0303",
  redSoft: "#fff0f0",
  warnBg: "#fff8e1",
  warnBorder: "#ffe0b2",
  warnTitle: "#e65100",
};

const REQUEST_TYPES = [
  { value: "GENERAL", label: "Help Request", hint: "Everyday task help", icon: <HomeOutlined /> },
  { value: "TRAVO", label: "Travo Help", hint: "Airport and travel help", icon: <CarOutlined /> },
  { value: "CARE", label: "Care Support", hint: "Non-medical care help", icon: <HeartOutlined /> },
];

const REQUEST_PAGE_COPY = {
  GENERAL: {
    title: "Help Request",
    subtitle: "Post everyday help requests the same way it starts in the Olitask app.",
    badge: "General task",
    icon: <HomeOutlined />,
  },
  TRAVO: {
    title: "Request Airport Luggage Help",
    subtitle: "Request airport, luggage, guidance, porter, or travel assistance.",
    badge: "Travel support",
    icon: <CarOutlined />,
  },
  CARE: {
    title: "Home Care & Assisted Living Request",
    subtitle: "Request non-medical care, companionship, home help, and assisted living support.",
    badge: "Care support",
    icon: <HeartOutlined />,
  },
};

const CARE_SERVICE_GROUPS = [
  {
    title: "Personal Care Support",
    services: [
      ["BATHING_ASSISTANCE", "Bathing Help"],
      ["DRESSING_GROOMING", "Dressing & Grooming"],
      ["TOILETING_SUPPORT", "Toileting Support"],
      ["BED_CHAIR_TRANSFER", "Bed/Chair Help"],
    ],
  },
  {
    title: "Daily Living Assistance",
    services: [
      ["MEAL_PREPARATION", "Meal Prep"],
      ["LIGHT_HOUSEKEEPING", "Light Housekeeping"],
      ["LAUNDRY", "Laundry"],
      ["SUPPLY_SUPPORT", "Supply Support"],
    ],
  },
  {
    title: "Mobility and Safety",
    services: [
      ["WALKING_ASSISTANCE", "Walking Help"],
      ["WHEELCHAIR_SUPPORT", "Wheelchair Support"],
      ["TRANSFER_ASSISTANCE", "Transfer Help"],
      ["FALL_PREVENTION", "Fall Prevention"],
    ],
  },
  {
    title: "Companionship",
    services: [
      ["CONVERSATION_SUPPORT", "Conversation"],
      ["READING_TOGETHER", "Reading Together"],
      ["GAMES_PUZZLES", "Games & Puzzles"],
      ["SOCIAL_VISIT", "Social Visit"],
    ],
  },
  {
    title: "Transportation and Errands",
    services: [
      ["APPOINTMENT_RIDES", "Appointment Rides"],
      ["PHARMACY_PICKUP", "Pharmacy Pickup"],
      ["GROCERY_PICKUP", "Grocery Pickup"],
      ["COMMUNITY_ESCORT", "Community Escort"],
    ],
  },
];

const TRAVO_SERVICE_TYPES = [
  ["AIRPORT", "✈️ Airport"],
  ["LUGGAGE", "🧳 Luggage"],
  ["GUIDANCE", "🧭 Guidance"],
  ["TRANSPORTATION", "🚘 Transportation"],
  ["PORTER", "🛎️ Porter"],
  ["ACCOMMODATION", "🏨 Accommodation"],
  ["GENERAL_TRAVEL", "🌍 General Travel"],
];

const TRAVO_MEETING_POINTS = [
  ["CURRENT_LOCATION", "My Current Location"],
  ["TERMINAL", "Terminal"],
  ["GATE", "Gate"],
  ["BAGGAGE_CLAIM", "Baggage Claim"],
  ["ARRIVAL_CURB", "Arrival Curb"],
  ["OTHER", "Other"],
];

const normalizeTravoServiceType = (value) => String(value || "AIRPORT").trim().toUpperCase();

const travoCategoryId = (category) => String(category?.id || "").trim();

const travoCategoryLabel = (category) =>
  clean(category?.name) ||
  clean(category?.label) ||
  clean(category?.title) ||
  clean(category?.description) ||
  "Travel assistance";

const travoMeetingPointId = (meetingPoint) => String(meetingPoint?.id || "").trim();

const travoMeetingPointKey = (meetingPoint) =>
  String(meetingPoint?.key ?? meetingPoint?.code ?? meetingPoint?.meetingPointKey ?? "").trim().toUpperCase();

const travoMeetingPointLabel = (meetingPoint) =>
  clean(meetingPoint?.name) ||
  clean(meetingPoint?.label) ||
  clean(meetingPoint?.title) ||
  travoMeetingPointKey(meetingPoint) ||
  "Meeting point";

const POPULAR_LANGUAGES = [
  "English",
  "Spanish",
  "French",
  "Portuguese",
  "German",
  "Italian",
  "Arabic",
  "Hindi",
  "Chinese (Mandarin)",
  "Japanese",
  "Korean",
  "Vietnamese",
  "Tagalog",
];

const PAY_OPTIONS = [
  ["HOURLY", "Hourly"],
  ["LUMP_SUM", "Lump Sum"],
];

const SCHEDULE_OPTIONS = [
  ["IMMEDIATE", "Now"],
  ["SCHEDULED", "Later"],
];

const DEFAULT_VISIBLE_CATEGORY_COUNT = 8;
const FEDERAL_MINIMUM_WAGE = 7.25;

const asList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray(value.content)) return value.content;
  if (Array.isArray(value.data)) return value.data;
  if (Array.isArray(value.items)) return value.items;
  return [];
};

const categoryLabel = (item) =>
  item?.name || item?.briefName || item?.label || item?.title || item?.code || item?.id || "Category";

const categoryValue = (item) => item?.id || item?.code || item?.name || item?.label;

const categoryChildren = (item) =>
  asList(item?.children || item?.subcategories || item?.subCategories || item?.nodes);

const isLeafCategory = (item) => {
  const type = String(item?.type || "").toUpperCase();
  if (type) return type === "LEAF";
  return categoryChildren(item).length === 0;
};

const flattenCategories = (items = [], path = []) =>
  asList(items).flatMap((item) => {
    const value = categoryValue(item);
    const label = categoryLabel(item);
    const nextPath = [...path, label];
    const children = categoryChildren(item);
    const option = value
      ? [{
          value,
          label,
          path: nextPath.join(" > "),
          type: isLeafCategory(item) ? "LEAF" : "BRANCH",
          raw: item,
        }]
      : [];
    return [...option, ...flattenCategories(children, nextPath)];
  });

const clean = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const compactObject = (object) =>
  Object.fromEntries(
    Object.entries(object || {}).filter(([, value]) => {
      if (value === undefined || value === null || value === "") return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object") return Object.keys(compactObject(value)).length > 0;
      return true;
    }),
  );

const compactPayload = (object) =>
  Object.fromEntries(
    Object.entries(object || {}).filter(([, value]) => {
      if (value === undefined || value === "") return false;
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        return Object.keys(compactPayload(value)).length > 0;
      }
      return true;
    }),
  );

const dateIso = (value) => {
  if (!value) return undefined;
  if (typeof value.toISOString === "function") return value.toISOString();
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : undefined;
};

const mobilePrefilledTravoWhen = () => dayjs().add(10, "minute").second(0).millisecond(0);
const mobilePrefilledCareStart = () => dayjs().add(2, "hour");
const mobilePrefilledCareEnd = () => dayjs().add(4, "hour");
const TASK_DATE_TIME_FORMAT = "MMM D, YYYY h:mm A";

const money = (value, currency = "USD") => {
  const numeric = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(numeric);
  } catch {
    return `${currency} ${numeric.toFixed(2)}`;
  }
};

const numericCoordinate = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return NaN;
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = LOCATION_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
};

const fetchIpLocationEstimate = async () => {
  const response = await fetchWithTimeout("https://ipapi.co/json/", {}, LOCATION_TIMEOUT_MS);
  if (!response.ok) throw new Error("Unable to estimate location.");
  const data = await response.json();
  const latitude = numericCoordinate(data.latitude, data.lat);
  const longitude = numericCoordinate(data.longitude, data.lon, data.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error("Unable to estimate location.");
  }
  const label = [data.city, data.region || data.region_code, data.country_name || data.country]
    .map(clean)
    .filter(Boolean)
    .join(", ");
  return {
    label: label || "your area",
    coords: { latitude, longitude },
  };
};

const haversineMiles = (lat1, lon1, lat2, lon2) => {
  const radiusMiles = 3958.8;
  const toRad = (degrees) => (degrees * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return radiusMiles * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const pickNearestAirport = (airports, userLat, userLon) => {
  let best = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const airport of asList(airports)) {
    if (airport?.active === false) continue;
    const code = clean(airport?.code) || clean(airport?.iataCode);
    if (!code) continue;
    const latitude = numericCoordinate(airport?.latitude);
    const longitude = numericCoordinate(airport?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    const distance = haversineMiles(userLat, userLon, latitude, longitude);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = airport;
    }
  }
  return best;
};

function RequiredLabel({ children }) {
  return (
    <Text className="olitask-required-label">
      {children} <span>*</span>
    </Text>
  );
}

export function GeneralTaskCreatePage() {
  return <TaskCreatePage fixedRequestType="GENERAL" />;
}

export function TravoTaskCreatePage() {
  return <TaskCreatePage fixedRequestType="TRAVO" />;
}

export function CareTaskCreatePage() {
  return <TaskCreatePage fixedRequestType="CARE" />;
}

function SectionCard({ title, subtitle, children, extra, error, className = "" }) {
  return (
    <Card
      className={`olitask-mobile-card${className ? ` ${className}` : ""}${error ? " is-error" : ""}`}
      title={
        <span className="olitask-section-heading">
          <Text className="olitask-section-title">{title}</Text>
          {subtitle ? <Text className="olitask-section-subtitle">{subtitle}</Text> : null}
        </span>
      }
      extra={extra}
      bodyStyle={{ padding: 12 }}
      headStyle={{ padding: "0 12px" }}
    >
      {children}
    </Card>
  );
}

function Chip({ children, selected, onClick, disabled }) {
  return (
    <button
      className={`olitask-mobile-chip${selected ? " is-selected" : ""}`}
      type="button"
      disabled={disabled}
      onClick={onClick}
    >
      {selected ? <CheckOutlined /> : null}
      {children}
    </button>
  );
}

function TypeCard({ item, selected, onClick }) {
  return (
    <button
      className={`olitask-type-card${selected ? " is-selected" : ""}`}
      type="button"
      onClick={onClick}
    >
      <Space align="start">
        <span
          className="olitask-type-card__icon"
        >
          {item.icon}
        </span>
        <span>
          <Text strong className={selected ? "olitask-themed-strong" : undefined}>
            {item.label}
          </Text>
          <br />
          <Text type="secondary">{item.hint}</Text>
        </span>
      </Space>
    </button>
  );
}

function Stepper({ label, value = 0, onChange }) {
  const numeric = Number(value || 0);
  return (
    <div
      className="olitask-stepper"
    >
      <Text strong>{label}</Text>
      <Space>
        <Button className="olitask-stepper__button" icon={<MinusOutlined />} onClick={() => onChange(Math.max(0, numeric - 1))} />
        <div className="olitask-stepper__value">
          {numeric}
        </div>
        <Button className="olitask-stepper__button" icon={<PlusOutlined />} onClick={() => onChange(Math.min(20, numeric + 1))} />
      </Space>
    </div>
  );
}

const categorySearch = (options, query) => {
  const text = String(query || "").trim().toLowerCase();
  const pool = options.filter((option) => String(option.type || "").toUpperCase() !== "ROOT");
  if (!text) return pool.slice(0, DEFAULT_VISIBLE_CATEGORY_COUNT);
  return pool
    .filter((option) =>
      [option.label, option.path].some((value) => String(value || "").toLowerCase().includes(text)),
    )
    .slice(0, 24);
};

const categoryNodePath = (roots, targetId) => {
  const walk = (nodes, path = []) => {
    for (const node of asList(nodes)) {
      const nextPath = [...path, node];
      if (categoryValue(node) === targetId) return nextPath;
      const found = walk(categoryChildren(node), nextPath);
      if (found.length) return found;
    }
    return [];
  };
  return walk(roots);
};

const uploadFiles = async ({ files, uploadFile, userId }) => {
  const selected = (files || []).map((entry) => entry.originFileObj).filter(Boolean);
  const uploaded = [];
  for (const file of selected) {
    const result = await uploadFile(userId, file, {
      maxBytes: 500_000,
      quality: 0.8,
      minQuality: 0.1,
      stepQuality: 0.1,
      skipUserValidation: true,
      includeUserId: false,
      preserveOriginal: false,
      preserveDimensions: true,
    });
    if (result?.fileId) uploaded.push(result.fileId);
  }
  return uploaded;
};

const locationPayload = (values, coords) =>
  compactObject({
    address: clean(values.location),
    latitude: coords?.latitude,
    longitude: coords?.longitude,
  });

const taskChargesPayload = (amount, extra = {}) => {
  const tokenAmount = Number(amount || 0);
  return compactPayload({
    currency: "USD",
    tokenAmount,
    receivableAmount: tokenAmount,
    chargeableAmount: tokenAmount,
    tipAmount: 0,
    useCredits: false,
    appliedCreditsTotal: 0,
    cashPortion: tokenAmount,
    creditDeductions: [],
    ...extra,
  });
};

const buildGeneralPayload = ({ values, photoIds, workspaceId, organizationId, coords, selectedCategoryIds }) => {
  const hours = Number(values.durationHours || 0);
  const minutes = Number(values.durationMinutes || 0);
  const duration = (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);
  const tokenAmount = Number(values.tokenAmount || 0);

  return compactPayload({
    title: values.title || "",
    description: clean(values.description),
    location: locationPayload(values, coords),
    requesterDuration: `${duration} minutes`,
    mainPhoto: photoIds[0] ?? null,
    photos: photoIds,
    categoryIds: selectedCategoryIds,
    tokenAmount,
    compensation: {
      payType: "LUMP_SUM",
      payRate: tokenAmount,
      estimatedTotal: tokenAmount,
      currency: "USD",
    },
    taskCharges: taskChargesPayload(tokenAmount),
    useCredits: false,
    appliedCredits: [],
    appliedCreditsTotal: 0,
    chargeableAmount: tokenAmount,
    receivableAmount: tokenAmount,
    workspaceId,
    requesterWorkspaceId: workspaceId,
    organizationId,
  });
};

const buildCarePayload = ({ values, photoIds, workspaceId, organizationId, coords, selectedCareServices }) => {
  const pay = Number(values.carePayAmount || values.tokenAmount || 0);
  const startAt = values.scheduleMode === "SCHEDULED" ? dateIso(values.startAt) : new Date().toISOString();
  const immediateDurationMinutes =
    Number(values.durationHours || 0) * 60 + Number(values.durationMinutes || 0);
  const startDate = values.startAt?.toDate?.() || values.startAt;
  const endDate = values.endAt?.toDate?.() || values.endAt;
  const endAt =
    values.scheduleMode === "SCHEDULED"
      ? dateIso(values.endAt)
      : new Date(Date.now() + immediateDurationMinutes * 60000).toISOString();
  const scheduledDurationMinutes =
    values.scheduleMode === "SCHEDULED" && startDate && endDate
      ? Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000))
      : 0;
  const durationMinutes = values.scheduleMode === "SCHEDULED" ? scheduledDurationMinutes : immediateDurationMinutes;
  const payType = values.payType || "HOURLY";
  const estimatedTotal = payType === "HOURLY" ? pay * (durationMinutes / 60) : pay;

  return compactPayload({
    taskType: "CARETASK",
    serviceCategory: "CARE_SUPPORT",
    services: selectedCareServices.map((service) => ({
      id: service.id,
      name: service.title,
      briefName: service.brief,
      category: service.category,
    })),
    entity: {
      type: clean(values.careLocationType || "HOME"),
      name: clean(values.location),
      organizationId,
    },
    schedule: {
      mode: values.scheduleMode || "IMMEDIATE",
      immediate: values.scheduleMode !== "SCHEDULED",
      startAt,
      endAt,
      durationMinutes,
    },
    compensation: {
      payType,
      payRate: pay,
      estimatedTotal,
      currency: "USD",
    },
    location: locationPayload(values, coords),
    description: clean(values.description),
    mainPhoto: photoIds[0] ?? null,
    photos: photoIds,
    workspaceId,
    requesterWorkspaceId: workspaceId,
    organizationId,
  });
};

const buildTravoPayload = ({ values, photoIds, workspaceId, organizationId, coords, selectedTravoServices, selectedTravoServiceType = "AIRPORT" }) => {
  const tokenAmount = Number(values.tokenAmount || 0);
  const serviceType = normalizeTravoServiceType(selectedTravoServiceType);
  const meetingPointId = values.meetingPointId || values.meetingPoint || "CURRENT_LOCATION";
  const useMyCurrentLocation = meetingPointId === "CURRENT_LOCATION";
  const duration = Number(values.durationMinutes || 60) + Number(values.durationHours || 0) * 60;
  const airportCode = clean(values.airportCode || "AIRPORT");
  const airportAddress = clean(values.airportAddress || `${airportCode} Airport`);
  const airportLatitude = Number(values.airportLatitude);
  const airportLongitude = Number(values.airportLongitude);
  const hasAirportCoords = Number.isFinite(airportLatitude) && Number.isFinite(airportLongitude);
  const mainPhoto = photoIds[0] ?? null;
  const taskCharges = taskChargesPayload(tokenAmount);

  return compactPayload({
    airportId: clean(values.airportId || airportCode),
    airportCode,
    scheduledAt: dateIso(values.scheduledAt) || new Date().toISOString(),
    meetingPointId,
    tokenAmount,
    currency: "USD",
    compensation: {
      payType: "LUMP_SUM",
      payRate: tokenAmount,
      estimatedTotal: tokenAmount,
      currency: "USD",
    },
    description: clean(values.description),
    meetingDetail: clean(values.meetingDetail),
    meetingNotes: clean(values.meetingNotes),
    terminal: values.needsTerminal ? clean(values.terminal) : undefined,
    gate: values.needsGate ? clean(values.gate) : undefined,
    meetingOtherDetail: values.needsMeetingOther ? clean(values.meetingOtherDetail) : undefined,
    meetingPingLocation:
      useMyCurrentLocation
        ? compactObject({
            latitude: coords?.latitude ?? (hasAirportCoords ? airportLatitude : undefined),
            longitude: coords?.longitude ?? (hasAirportCoords ? airportLongitude : undefined),
            address: clean(values.meetingDetail) || clean(values.location) || airportAddress,
          })
        : undefined,
    serviceType,
    serviceCategoryIds: selectedTravoServices,
    flightNumber: clean(values.flightNumber),
    smallBags: Number(values.smallBags || 0),
    mediumBags: Number(values.mediumBags || 0),
    largeBags: Number(values.largeBags || 0),
    fragile: Boolean(values.fragile),
    heavy: Boolean(values.heavy),
    escortToGate: Boolean(values.escortToGate),
    wheelchair: Boolean(values.wheelchair),
    language: values.language || "English",
    languageRequired: false,
    photos: photoIds,
    taskCreateRequest: {
      title: null,
      categoryIds: TRAVO_TASK_CATEGORY_IDS,
      description: clean(values.description),
      location: compactObject({
        latitude: hasAirportCoords ? airportLatitude : undefined,
        longitude: hasAirportCoords ? airportLongitude : undefined,
        address: airportAddress,
      }),
      requesterDuration: `${duration} minutes`,
      mainPhoto,
      photos: photoIds,
      taskCharges,
      tokenAmount,
      compensation: {
        payType: "LUMP_SUM",
        payRate: tokenAmount,
        estimatedTotal: tokenAmount,
        currency: "USD",
      },
      chargeableAmount: tokenAmount,
      receivableAmount: tokenAmount,
      workspaceId,
      requesterWorkspaceId: workspaceId,
      organizationId,
    },
    workspaceId,
    requesterWorkspaceId: workspaceId,
    organizationId,
  });
};

export default function TaskCreatePage({ fixedRequestType = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, selectedWorkspaceUse } = useWorkspace();
  const { categories, loading: categoriesLoading, fetchAllCategories } = useTaskCategories();
  const { airports, loading: airportsLoading, fetchAirports } = useAirports(0, 500);
  const {
    categories: travoCategories,
    loading: travoCategoriesLoading,
    error: travoCategoriesError,
    fetchCategories: fetchTravoCategories,
  } = useTravelTaskCategories();
  const {
    meetingPoints,
    loading: meetingPointsLoading,
    error: meetingPointsError,
    fetchMeetingPoints,
  } = useMeetingPoints();
  const { uploadFile, uploading: uploadingFile } = useFileUpload();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const routeRequestType = String(fixedRequestType || "").toUpperCase();
  const stateRequestType = String(location.state?.requestType || "").toUpperCase();
  const initialRequestType = ["GENERAL", "CARE", "TRAVO"].includes(routeRequestType)
    ? routeRequestType
    : ["GENERAL", "CARE", "TRAVO"].includes(stateRequestType)
      ? stateRequestType
      : "GENERAL";
  const isFixedRequestType = Boolean(routeRequestType);
  const [requestType, setRequestType] = useState(initialRequestType);
  const [categoryQuery, setCategoryQuery] = useState("");
  const [categoryPath, setCategoryPath] = useState([]);
  const [currentCategoryOptions, setCurrentCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedCareIds, setSelectedCareIds] = useState([]);
  const [selectedTravoServices, setSelectedTravoServices] = useState([]);
  const [selectedTravoServiceType, setSelectedTravoServiceType] = useState("AIRPORT");
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [autoSelectedNearestAirport, setAutoSelectedNearestAirport] = useState(false);
  const [airportsLoadedOnce, setAirportsLoadedOnce] = useState(false);
  const [photoList, setPhotoList] = useState([]);
  const [locationInput, setLocationInput] = useState("");
  const [locationMode, setLocationMode] = useState("MANUAL");
  const [coords, setCoords] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [appConfigGoogleMapsApiKey, setAppConfigGoogleMapsApiKey] = useState("");
  const [appConfigGoogleMapsApiKeyLoading, setAppConfigGoogleMapsApiKeyLoading] = useState(false);
  const [bags, setBags] = useState({ smallBags: 0, mediumBags: 0, largeBags: 0 });
  const [fragile, setFragile] = useState(false);
  const [heavy, setHeavy] = useState(false);

  const watchedValues = Form.useWatch([], form) || {};
  const pageCopy = REQUEST_PAGE_COPY[requestType] || REQUEST_PAGE_COPY.GENERAL;
  const flowClassName = `olitask-mobile-flow is-${String(requestType || "GENERAL").toLowerCase()}`;
  const envGoogleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const googleMapsApiKey =
    envGoogleMapsApiKey ||
    appConfigGoogleMapsApiKey ||
    "";
  const categoryRoots = useMemo(() => asList(categories), [categories]);
  const categoryOptions = useMemo(() => flattenCategories(categoryRoots), [categoryRoots]);
  const selectedCategoryIds = useMemo(
    () => selectedCategories.map((item) => item.value).filter(Boolean),
    [selectedCategories],
  );
  const searchedCategories = useMemo(
    () => categorySearch(categoryOptions, categoryQuery),
    [categoryOptions, categoryQuery],
  );
  const airportOptions = useMemo(
    () =>
      asList(airports).map((airport) => {
        const code = clean(airport?.code) || clean(airport?.iataCode) || clean(airport?.id) || "";
        const name = clean(airport?.name) || clean(airport?.airportName) || "";
        const city = clean(airport?.city) || "";
        const state = clean(airport?.state) || "";
        const label = [code, name, [city, state].filter(Boolean).join(", ")].filter(Boolean).join(" - ");
        return {
          value: airport?.id || code,
          label: label || code || "Airport",
          airport,
        };
      }).filter((option) => option.value),
    [airports],
  );
  const availableTravoServices = useMemo(
    () =>
      asList(travoCategories)
        .filter((category) => {
          const id = travoCategoryId(category);
          const serviceType = normalizeTravoServiceType(category?.serviceType);
          return id && serviceType === selectedTravoServiceType && category?.active !== false;
        }),
    [selectedTravoServiceType, travoCategories],
  );
  const selectedTravoServiceRows = useMemo(
    () => {
      const serviceById = new Map(asList(travoCategories).map((category) => [travoCategoryId(category), category]));
      return selectedTravoServices.map((id) => serviceById.get(id)).filter(Boolean);
    },
    [selectedTravoServices, travoCategories],
  );
  const filteredMeetingPoints = useMemo(
    () =>
      asList(meetingPoints)
        .map((meetingPoint) => ({
          ...meetingPoint,
          id: travoMeetingPointId(meetingPoint),
          key: travoMeetingPointKey(meetingPoint),
          name: travoMeetingPointLabel(meetingPoint),
        }))
        .filter((meetingPoint) => {
          if (!meetingPoint.id) return false;
          if (meetingPoint.active === false) return false;
          if (!selectedAirport?.id || !meetingPoint.airportId) return true;
          return String(meetingPoint.airportId) === String(selectedAirport.id);
        }),
    [meetingPoints, selectedAirport?.id],
  );
  const currentMeetingPoint =
    watchedValues.meetingPointId === "CURRENT_LOCATION"
      ? { id: "CURRENT_LOCATION", key: "CURRENT_LOCATION", name: "📍 My Current Location" }
      : filteredMeetingPoints.find((meetingPoint) => meetingPoint.id === watchedValues.meetingPointId);
  const currentMeetingPointKey = travoMeetingPointKey(currentMeetingPoint);
  const visibleTreeCategories = useMemo(() => {
    if (showAllCategories) return currentCategoryOptions;
    return currentCategoryOptions.slice(0, DEFAULT_VISIBLE_CATEGORY_COUNT);
  }, [currentCategoryOptions, showAllCategories]);
  const hasMoreTreeCategories = currentCategoryOptions.length > DEFAULT_VISIBLE_CATEGORY_COUNT;

  useEffect(() => {
    setCurrentCategoryOptions(categoryRoots);
    setCategoryPath([]);
  }, [categoryRoots]);

  useEffect(() => {
    setShowAllCategories(Boolean(categoryQuery.trim()));
  }, [categoryQuery]);

  useEffect(() => {
    if (requestType === "TRAVO" && !airportsLoadedOnce && !airportsLoading) {
      fetchAirports().finally(() => setAirportsLoadedOnce(true));
    }
  }, [airportsLoadedOnce, airportsLoading, fetchAirports, requestType]);

  useEffect(() => {
    if (requestType !== "TRAVO") return;
    fetchTravoCategories();
    fetchMeetingPoints();
  }, [fetchMeetingPoints, fetchTravoCategories, requestType]);

  const allCareServices = useMemo(
    () =>
      CARE_SERVICE_GROUPS.flatMap((group) =>
        group.services.map(([id, brief]) => ({
          id,
          title: brief,
          brief,
          category: group.title,
        })),
      ),
    [],
  );

  const selectedCareServices = useMemo(
    () => allCareServices.filter((service) => selectedCareIds.includes(service.id)),
    [allCareServices, selectedCareIds],
  );

  const durationMinutes =
    Number(watchedValues.durationHours || 0) * 60 +
    Number(watchedValues.durationMinutes || 0);
  const suggestedAmount = Math.max(0, (durationMinutes / 60) * FEDERAL_MINIMUM_WAGE + (durationMinutes > 0 ? 6 : 0));
  const tokenAmount = Number(watchedValues.tokenAmount || watchedValues.carePayAmount || 0);
  const estimatedFees = tokenAmount > 0 ? Math.max(1.25, tokenAmount * 0.08) : 0;
  const isHelperContext = String(selectedWorkspaceUse || "").toUpperCase() === "HELPER";
  const totalBags = Number(bags.smallBags || 0) + Number(bags.mediumBags || 0) + Number(bags.largeBags || 0);
  const travoServicesSummary = selectedTravoServiceRows.length
    ? selectedTravoServiceRows
        .map((category) => travoCategoryLabel(category))
        .slice(0, 2)
        .join(", ") + (selectedTravoServiceRows.length > 2 ? ` +${selectedTravoServiceRows.length - 2}` : "")
    : "None selected";
  const travoBagsSummary = `${totalBags} bag${totalBags === 1 ? "" : "s"}${fragile ? " • Fragile" : ""}${heavy ? " • Heavy" : ""}`;
  const travoMeetingSummary = currentMeetingPoint?.name || "Select meeting point";
  const travoScheduledAt = watchedValues.scheduledAt || form.getFieldValue("scheduledAt");
  const travoScheduleSummary = travoScheduledAt
    ? travoScheduledAt.format?.(TASK_DATE_TIME_FORMAT) || "Scheduled"
    : "Select time";
  const travoLanguageSummary = [
    watchedValues.language || "English",
    watchedValues.escortToGate ? "Escort" : "",
    watchedValues.wheelchair ? "Wheelchair" : "",
  ].filter(Boolean).join(" • ");
  const travoDescriptionSummary = watchedValues.description?.trim()
    ? watchedValues.description.trim().slice(0, 44)
    : "Add task details";
  const travoFeesSummary = tokenAmount > 0
    ? `Pay ${money(tokenAmount + estimatedFees)}`
    : "Enter amount";

  useEffect(() => {
    if (isHelperContext) {
      message.info("Switch to Requester context to submit a help request.");
    }
  }, [isHelperContext]);

  useEffect(() => {
    if (envGoogleMapsApiKey || appConfigGoogleMapsApiKey) return;

    let cancelled = false;
    const loadAppConfig = async () => {
      try {
        setAppConfigGoogleMapsApiKeyLoading(true);
        const session = await ensureGuestSession();
        const token = bearerGuestToken(session);
        const response = await apiClient.get("/olitask-service/api/v1/app-config", {
          headers: {
            ...(token ? { Authorization: token } : {}),
            "O-SKIP-WORKSPACE-CONTEXT": "1",
          },
        });
        if (cancelled) return;
        setAppConfigGoogleMapsApiKey(googleMapsKeyFromConfigResponse(response));
      } catch {
        if (!cancelled) setAppConfigGoogleMapsApiKey("");
      } finally {
        if (!cancelled) setAppConfigGoogleMapsApiKeyLoading(false);
      }
    };

    loadAppConfig();

    return () => {
      cancelled = true;
    };
  }, [appConfigGoogleMapsApiKey, envGoogleMapsApiKey]);

  const applyAirportSelection = useCallback(
    (airport) => {
      if (!airport) return false;

      const code = clean(airport.code) || clean(airport.iataCode) || clean(airport.id) || "";
      const address =
        clean(airport.address) ||
        [airport.name, airport.city, airport.state, airport.country].map(clean).filter(Boolean).join(", ") ||
        `${code} Airport`;
      const latitude = Number(airport.latitude);
      const longitude = Number(airport.longitude);

      setSelectedAirport(airport);
      form.setFieldsValue({
        airportId: airport.id || code,
        airportCode: code,
        airportAddress: address,
        airportLatitude: Number.isFinite(latitude) ? latitude : undefined,
        airportLongitude: Number.isFinite(longitude) ? longitude : undefined,
        meetingPointId: "CURRENT_LOCATION",
        meetingPoint: "CURRENT_LOCATION",
      });
      return true;
    },
    [form],
  );

  const selectNearestAirportFromCoords = useCallback(
    (nextCoords) => {
      if (requestType !== "TRAVO") return false;
      if (!Number.isFinite(nextCoords?.latitude) || !Number.isFinite(nextCoords?.longitude)) return false;
      const nearest = pickNearestAirport(airports, nextCoords.latitude, nextCoords.longitude);
      return applyAirportSelection(nearest);
    },
    [airports, applyAirportSelection, requestType],
  );

  const setCurrentLocation = () => {
    setLocationMode("CURRENT");
    if (!navigator.geolocation) {
      if (requestType === "TRAVO") {
        fetchIpLocationEstimate()
          .then(({ label, coords: estimateCoords }) => {
            setCoords(estimateCoords);
            setLocationInput(label);
            form.setFieldsValue({ location: label });
            selectNearestAirportFromCoords(estimateCoords);
            message.warning("Precise location was not available, so we used your estimated area.");
          })
          .catch(() => message.warning("Current location is not available in this browser."));
      } else {
        message.warning("Current location is not available in this browser.");
      }
      return;
    }

    setLocationLoading(true);
    let locationSettled = false;
    const locationTimer = window.setTimeout(() => {
      if (locationSettled || requestType !== "TRAVO") return;
      locationSettled = true;
      fetchIpLocationEstimate()
        .then(({ label, coords: estimateCoords }) => {
          setCoords(estimateCoords);
          setLocationInput(label);
          form.setFieldsValue({ location: label });
          selectNearestAirportFromCoords(estimateCoords);
          message.warning("Precise location was not available, so we used your estimated area.");
        })
        .catch(() => message.error("Could not read current location. Enter a location instead."))
        .finally(() => setLocationLoading(false));
    }, LOCATION_TIMEOUT_MS + 1500);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (locationSettled) return;
        locationSettled = true;
        window.clearTimeout(locationTimer);
        const nextCoords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setCoords(nextCoords);
        setLocationInput("Current Location");
        form.setFieldsValue({
          location: "Current Location",
        });
        selectNearestAirportFromCoords(nextCoords);
        setLocationLoading(false);
      },
      () => {
        if (locationSettled) return;
        locationSettled = true;
        window.clearTimeout(locationTimer);
        if (requestType === "TRAVO") {
          fetchIpLocationEstimate()
            .then(({ label, coords: estimateCoords }) => {
              setCoords(estimateCoords);
              setLocationInput(label);
              form.setFieldsValue({ location: label });
              selectNearestAirportFromCoords(estimateCoords);
              message.warning("Precise location was not available, so we used your estimated area.");
            })
            .catch(() => {
              message.error("Could not read current location. Enter a location instead.");
              setLocationMode("MANUAL");
            })
            .finally(() => setLocationLoading(false));
          return;
        }
        message.error("Could not read current location. Enter a location instead.");
        setLocationLoading(false);
        setLocationMode("MANUAL");
      },
      { enableHighAccuracy: true, timeout: LOCATION_TIMEOUT_MS, maximumAge: 60000 },
    );
  };

  useEffect(() => {
    if (requestType !== "TRAVO") return;
    if (!airportsLoadedOnce) return;
    if (!asList(airports).length) return;
    if (selectedAirport?.id || selectedAirport?.code) return;
    if (autoSelectedNearestAirport) return;

    let cancelled = false;
    const finish = () => {
      if (!cancelled) setAutoSelectedNearestAirport(true);
    };
    const loadEstimatedAirportLocation = async () => {
      try {
        const { label, coords: estimateCoords } = await fetchIpLocationEstimate();
        if (cancelled) return;
        setCoords(estimateCoords);
        setLocationInput(label);
        form.setFieldsValue({ location: label });
        selectNearestAirportFromCoords(estimateCoords);
      } catch {
        // Matching mobile behavior: silently stop auto-selection when no usable location exists.
      } finally {
        finish();
      }
    };

    if (!navigator.geolocation) {
      loadEstimatedAirportLocation();
      return () => {
        cancelled = true;
      };
    }

    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      loadEstimatedAirportLocation();
    }, LOCATION_TIMEOUT_MS + 1500);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (settled || cancelled) return;
        settled = true;
        window.clearTimeout(timer);
        const nextCoords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setCoords(nextCoords);
        setLocationInput("Current Location");
        form.setFieldsValue({ location: "Current Location" });
        selectNearestAirportFromCoords(nextCoords);
        finish();
      },
      () => {
        if (settled || cancelled) return;
        settled = true;
        window.clearTimeout(timer);
        loadEstimatedAirportLocation();
      },
      { enableHighAccuracy: true, timeout: LOCATION_TIMEOUT_MS, maximumAge: 60000 },
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    airports,
    airportsLoadedOnce,
    applyAirportSelection,
    autoSelectedNearestAirport,
    form,
    requestType,
    selectNearestAirportFromCoords,
    selectedAirport?.code,
    selectedAirport?.id,
  ]);

  const handleGoogleAddressSelect = useCallback(
    ({ details, prediction }) => {
      const selectedText = String(prediction?.description || "").trim();
      if (selectedText.toLowerCase() === "current location") {
        setCurrentLocation();
        return;
      }

      const fields = addressFieldsFromPlaceDetails(details);
      const formattedAddress =
        details?.formatted_address ||
        selectedText ||
        form.getFieldValue("location") ||
        "";

      setLocationMode("MANUAL");
      setLocationInput(formattedAddress);
      form.setFieldsValue({
        location: formattedAddress,
        country: fields.country || "US",
      });

      if (Number.isFinite(fields.latitude) && Number.isFinite(fields.longitude)) {
        setCoords({
          latitude: fields.latitude,
          longitude: fields.longitude,
        });
      }
    },
    [form],
  );

  const toggleCategory = (id) => {
    const option = categoryOptions.find((item) => item.value === id);
    if (!option || option.type !== "LEAF") return;
    setSelectedCategories((current) =>
      current.some((item) => item.value === id)
        ? current.filter((item) => item.value !== id)
        : [...current, option],
    );
  };

  const chooseCategoryNode = (node) => {
    const value = categoryValue(node);
    if (!value) return;
    if (isLeafCategory(node)) {
      toggleCategory(value);
      return;
    }
    setCategoryPath((current) => [...current, node]);
    setCurrentCategoryOptions(categoryChildren(node));
    setCategoryQuery("");
    setShowAllCategories(false);
  };

  const jumpToCategoryBranch = (id) => {
    const path = categoryNodePath(categoryRoots, id);
    if (!path.length) return;
    const target = path[path.length - 1];
    if (isLeafCategory(target)) {
      toggleCategory(id);
      return;
    }
    setCategoryPath(path);
    setCurrentCategoryOptions(categoryChildren(target));
    setCategoryQuery("");
    setShowAllCategories(false);
  };

  const onCrumbPress = (index) => {
    if (index < 0) {
      setCategoryPath([]);
      setCurrentCategoryOptions(categoryRoots);
      setShowAllCategories(false);
      return;
    }
    const nextPath = categoryPath.slice(0, index + 1);
    const target = nextPath[nextPath.length - 1];
    setCategoryPath(nextPath);
    setCurrentCategoryOptions(categoryChildren(target));
    setShowAllCategories(false);
  };

  const resetCategory = () => {
    setCategoryPath([]);
    setCurrentCategoryOptions(categoryRoots);
    setCategoryQuery("");
    setShowAllCategories(false);
  };

  const toggleCare = (id) => {
    setSelectedCareIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const toggleTravoService = (id) => {
    setSelectedTravoServices((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const selectTravoServiceType = (type) => {
    const nextType = normalizeTravoServiceType(type);
    setSelectedTravoServiceType(nextType);
    setSelectedTravoServices([]);
  };

  const selectAirport = (airportId) => {
    const option = airportOptions.find((item) => item.value === airportId);
    applyAirportSelection(option?.airport);
    setAutoSelectedNearestAirport(true);
  };

  const validateMobileRequired = (values) => {
    if (isHelperContext) {
      return "Switch to Requester context to submit a help request.";
    }
    if (!selectedWorkspaceId && !selectedWorkspace?.id) {
      return "Please select a workspace before submitting a help request.";
    }
    if (requestType === "GENERAL" && selectedCategoryIds.length === 0) {
      return "Select at least one task category.";
    }
    if (requestType === "CARE" && selectedCareIds.length === 0) {
      return "Select at least one care service.";
    }
    if (requestType === "TRAVO" && selectedTravoServices.length === 0) {
      return "Select at least one Travo service.";
    }
    if (requestType === "TRAVO" && (!values.airportId || !values.airportCode?.trim())) return "Select an airport.";
    if (requestType === "TRAVO" && !values.meetingPointId) return "Meeting point is required.";
    if (requestType === "TRAVO" && values.meetingPointId === "CURRENT_LOCATION" && !coords) {
      return "Use current location for this meeting point.";
    }
    if (requestType === "TRAVO" && currentMeetingPointKey === "TERMINAL" && !values.terminal?.trim()) return "Terminal is required.";
    if (requestType === "TRAVO" && currentMeetingPointKey === "GATE" && !values.gate?.trim()) return "Gate is required.";
    if (requestType === "TRAVO" && currentMeetingPointKey === "OTHER" && !values.meetingOtherDetail?.trim()) {
      return "Other meeting point is required.";
    }
    if (requestType === "TRAVO" && !values.scheduledAt) return "When is required.";
    if (requestType === "TRAVO" && (selectedTravoServiceType === "LUGGAGE" || selectedTravoServiceType === "AIRPORT") && totalBags <= 0) {
      return "At least one bag is required for this request.";
    }
    if (requestType !== "CARE" && !values.description?.trim()) return "Description is required.";
    if (requestType !== "CARE" && !photoList.length) return "At least one photo is required.";
    if (requestType !== "TRAVO" && !values.location?.trim()) return "Task location is required.";
    if (requestType !== "TRAVO" && !coords) return "Select a location suggestion or use current location.";
    if (requestType !== "CARE" && durationMinutes <= 0) return "Duration is required.";
    if (requestType === "CARE" && values.scheduleMode === "SCHEDULED" && (!values.startAt || !values.endAt)) {
      return "Start and end time are required.";
    }
    if (requestType === "CARE" && values.scheduleMode === "SCHEDULED") {
      const startDate = values.startAt?.toDate?.() || values.startAt;
      const endDate = values.endAt?.toDate?.() || values.endAt;
      if (startDate && endDate && endDate <= startDate) return "End time must be after start time.";
    }
    if (requestType === "CARE" && values.scheduleMode !== "SCHEDULED" && durationMinutes <= 0) {
      return "Duration is required.";
    }
    if (tokenAmount <= 0) return "Token Amount is required.";
    return "";
  };

  const submit = async (values) => {
    const validationError = validateMobileRequired(values);
    if (validationError) {
      setSubmitError(validationError);
      message.error(validationError);
      return;
    }

    const workspaceId = selectedWorkspaceId || selectedWorkspace?.id;
    const organizationId = selectedWorkspace?.organizationId;

    setSubmitting(true);
    setSubmitError("");
    try {
      const photoIds = await uploadFiles({
        files: photoList,
        uploadFile,
        userId: user?.id,
      });

      let payload;
      if (requestType === "CARE") {
        payload = buildCarePayload({
          values,
          photoIds,
          workspaceId,
          organizationId,
          coords,
          selectedCareServices,
        });
      } else if (requestType === "TRAVO") {
        payload = buildTravoPayload({
          values: {
            ...values,
            ...bags,
            fragile,
            heavy,
            needsTerminal: currentMeetingPointKey === "TERMINAL",
            needsGate: currentMeetingPointKey === "GATE",
            needsMeetingOther: currentMeetingPointKey === "OTHER",
          },
          photoIds,
          workspaceId,
          organizationId,
          coords,
          selectedTravoServices,
          selectedTravoServiceType,
        });
      } else {
        payload = buildGeneralPayload({
          values,
          photoIds,
          workspaceId,
          organizationId,
          coords,
          selectedCategoryIds,
        });
      }

      await createTaskRequest(payload, requestType);
      message.success("Help request submitted.");
      navigate(paths.myTasks);
    } catch (error) {
      const nextError =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong while submitting this help request.";
      setSubmitError(nextError);
      message.error(nextError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={flowClassName}>
      <div className="olitask-mobile-shell">
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
          {requestType === "TRAVO" ? (
            <div className="olitask-travo-screen-header">
              <Title level={2} className="olitask-request-title">
                Request Airport Luggage Help
              </Title>
              <Text className="olitask-request-subtitle">
                Service: {TRAVO_SERVICE_TYPES.find(([value]) => value === selectedTravoServiceType)?.[1] || "✈️ Airport"} Help
              </Text>
            </div>
          ) : (
            <div className="olitask-mobile-header">
              <div className="olitask-request-hero">
                <div className="olitask-request-hero-main">
                  <span className="olitask-request-hero-icon">{pageCopy.icon}</span>
                  <div className="olitask-request-hero-copy">
                    <span className="olitask-request-type-badge">
                      {pageCopy.badge}
                    </span>
                    <Title level={2} className="olitask-request-title">
                      {pageCopy.title}
                    </Title>
                    <Text className="olitask-request-subtitle">
                      {pageCopy.subtitle}
                    </Text>
                  </div>
                </div>
                <Button className="olitask-hero-action" icon={<ReloadOutlined />} onClick={fetchAllCategories}>
                  Refresh Categories
                </Button>
              </div>
            </div>
          )}

          {submitError ? (
            <Alert showIcon type="error" message="Review needed" description={submitError} />
          ) : null}
          {isHelperContext ? (
            <Alert
              showIcon
              type="warning"
              message="Requester context required"
              description="Helper context is for finding and working on requests. Switch to Requester context to submit a new help request."
            />
          ) : null}

          <Form
            form={form}
            layout="vertical"
            onFinish={submit}
            initialValues={{
              requestType: initialRequestType,
              locationMode: "MANUAL",
              durationHours: null,
              durationMinutes: null,
              tokenAmount: null,
              scheduledAt: mobilePrefilledTravoWhen(),
              startAt: mobilePrefilledCareStart(),
              endAt: mobilePrefilledCareEnd(),
              scheduleMode: "IMMEDIATE",
              payType: "HOURLY",
              meetingPoint: "CURRENT_LOCATION",
              meetingPointId: "CURRENT_LOCATION",
              meetingNotes: "",
              language: "English",
              country: "US",
            }}
            requiredMark={false}
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              {!isFixedRequestType ? (
                <SectionCard title="Request Type">
                  <Row gutter={[12, 12]}>
                    {REQUEST_TYPES.map((item) => (
                      <Col key={item.value} xs={24} md={8}>
                        <TypeCard
                          item={item}
                          selected={requestType === item.value}
                          onClick={() => {
                            setRequestType(item.value);
                            form.setFieldsValue({ requestType: item.value });
                          }}
                        />
                      </Col>
                    ))}
                  </Row>
                </SectionCard>
              ) : null}

              {requestType === "GENERAL" ? (
                <SectionCard
                  title={<RequiredLabel>Select Task Categories</RequiredLabel>}
                  extra={
                    <Button className="olitask-link-btn" type="link" onClick={resetCategory}>
                      Reset
                    </Button>
                  }
                >
                  <Input
                    allowClear
                    value={categoryQuery}
                    onChange={(event) => setCategoryQuery(event.target.value)}
                    placeholder="Search task categories e.g. cleaning, laundry..."
                    style={{ borderRadius: 14, marginBottom: 12 }}
                  />

                  {categoryQuery.trim() ? (
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      {categoriesLoading ? (
                        <Text type="secondary">Loading...</Text>
                      ) : searchedCategories.length === 0 ? (
                        <Text type="secondary">No matches.</Text>
                      ) : (
                        <Space size={[8, 8]} wrap>
                          {searchedCategories.map((option) => {
                            const selected = option.type === "LEAF" && selectedCategoryIds.includes(option.value);
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() =>
                                  option.type === "LEAF"
                                    ? toggleCategory(option.value)
                                    : jumpToCategoryBranch(option.value)
                                }
                                className={`olitask-category-result ${option.type === "LEAF" ? "is-leaf" : "is-branch"}${selected ? " is-selected" : ""}`}
                              >
                                <Text strong>
                                  {option.label} {option.type === "LEAF" ? (selected ? "✓" : "") : "(branch)"}
                                </Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  {option.path}
                                </Text>
                              </button>
                            );
                          })}
                        </Space>
                      )}
                    </Space>
                  ) : (
                    <>
                      <Space size={6} wrap style={{ marginBottom: 12 }}>
                        <Button
                          type="link"
                          onClick={() => onCrumbPress(-1)}
                          className={`olitask-crumb${categoryPath.length === 0 ? " is-active" : ""}`}
                        >
                          Tasks Categories
                        </Button>
                        {categoryPath.map((node, index) => (
                          <Space key={categoryValue(node) || index} size={6}>
                            <Text type="secondary">&gt;</Text>
                            <Button
                              type="link"
                              onClick={() => onCrumbPress(index)}
                              className={`olitask-crumb${index === categoryPath.length - 1 ? " is-active" : ""}`}
                            >
                              {categoryLabel(node)}
                            </Button>
                          </Space>
                        ))}
                      </Space>

                      <Space size={[8, 8]} wrap>
                        {categoriesLoading ? (
                          <Text type="secondary">Loading categories...</Text>
                        ) : (
                          visibleTreeCategories.map((node) => {
                            const value = categoryValue(node);
                            const leaf = isLeafCategory(node);
                            const selected = leaf && selectedCategoryIds.includes(value);
                            return (
                              <Chip
                                key={value}
                                selected={selected}
                                onClick={() => chooseCategoryNode(node)}
                              >
                                {categoryLabel(node)}
                                {leaf ? null : <RightOutlined />}
                              </Chip>
                            );
                          })
                        )}
                        {!categoriesLoading && hasMoreTreeCategories ? (
                          <Chip selected={false} onClick={() => setShowAllCategories((value) => !value)}>
                            {showAllCategories
                              ? "Show less"
                              : `View ${currentCategoryOptions.length - DEFAULT_VISIBLE_CATEGORY_COUNT} more`}
                          </Chip>
                        ) : null}
                      </Space>
                    </>
                  )}

                  {selectedCategories.length ? (
                    <div style={{ marginTop: 10 }}>
                      <Text strong className="olitask-themed-strong">
                        Selected
                      </Text>
                      <div style={{ marginTop: 8 }}>
                        <Space size={[8, 8]} wrap>
                          {selectedCategories.map((option) => {
                            return (
                              <Chip key={option.value} selected onClick={() => toggleCategory(option.value)}>
                                {option.label} <CloseOutlined />
                              </Chip>
                            );
                          })}
                        </Space>
                      </div>
                    </div>
                  ) : null}
                </SectionCard>
              ) : null}

              {requestType === "CARE" ? (
                <SectionCard title={<RequiredLabel>Select Care Services</RequiredLabel>}>
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {CARE_SERVICE_GROUPS.map((group) => (
                      <div
                        key={group.title}
                        style={{
                          background: "var(--olitask-green-pale)",
                          border: "1px solid var(--olitask-line)",
                          borderRadius: 12,
                          padding: 10,
                        }}
                      >
                        <Text strong className="olitask-themed-strong">
                          {group.title}
                        </Text>
                        <div style={{ marginTop: 8 }}>
                          <Space size={[8, 8]} wrap>
                            {group.services.map(([id, label]) => (
                              <Chip key={id} selected={selectedCareIds.includes(id)} onClick={() => toggleCare(id)}>
                                {label}
                              </Chip>
                            ))}
                          </Space>
                        </div>
                      </div>
                    ))}
                  </Space>
                </SectionCard>
              ) : null}

              {requestType === "TRAVO" ? (
                <SectionCard
                  className="olitask-travo-card"
                  title={<RequiredLabel>Select Airport</RequiredLabel>}
                  subtitle={
                    selectedAirport
                      ? `${selectedAirport.code || selectedAirport.iataCode} - ${selectedAirport.name || "Airport"}`
                      : airportsLoading
                        ? "Loading airports..."
                        : "Search airport..."
                  }
                >
                  <Form.Item name="airportId" style={{ marginBottom: 6 }}>
                    <Select
                      showSearch
                      loading={airportsLoading}
                      options={airportOptions}
                      optionFilterProp="label"
                      placeholder={airportsLoading ? "Loading airports..." : "Search by code, city, or airport name"}
                      onChange={selectAirport}
                    />
                  </Form.Item>
                  <Form.Item name="airportCode" hidden><Input /></Form.Item>
                  <Form.Item name="airportAddress" hidden><Input /></Form.Item>
                  <Form.Item name="airportLatitude" hidden><Input /></Form.Item>
                  <Form.Item name="airportLongitude" hidden><Input /></Form.Item>
                  {autoSelectedNearestAirport && selectedAirport ? (
                    <Text className="olitask-muted-copy" style={{ fontSize: 11 }}>
                      Nearest airport selected based on your location or previous location selection.
                    </Text>
                  ) : null}
                </SectionCard>
              ) : null}

              {requestType === "TRAVO" ? (
                <SectionCard
                  className="olitask-travo-card"
                  title="🧩 Select Need Assistance"
                  subtitle={travoServicesSummary}
                >
                  <Text className="olitask-travo-help">
                    Showing: <strong>{TRAVO_SERVICE_TYPES.find(([value]) => value === selectedTravoServiceType)?.[1] || "Airport"}</strong>
                  </Text>
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {travoCategoriesError ? (
                      <Alert showIcon type="warning" message="Unable to load assistance services" description={travoCategoriesError.message} />
                    ) : null}
                    <Space size={[8, 8]} wrap>
                      {travoCategoriesLoading ? (
                        <Text type="secondary">Loading assistance services...</Text>
                      ) : availableTravoServices.length ? (
                        availableTravoServices.map((service) => {
                          const id = travoCategoryId(service);
                          return (
                            <Chip
                              key={id}
                              selected={selectedTravoServices.includes(id)}
                              onClick={() => toggleTravoService(id)}
                            >
                              {travoCategoryLabel(service)}
                            </Chip>
                          );
                        })
                      ) : (
                        <Text type="secondary">No assistance services available.</Text>
                      )}
                    </Space>
                  </Space>
                </SectionCard>
              ) : null}

              {requestType !== "TRAVO" ? (
                <SectionCard title={<RequiredLabel>Description & Photos</RequiredLabel>}>
                  <Form.Item name="description" style={{ marginBottom: 8 }}>
                    <Input.TextArea
                      rows={4}
                      placeholder="Enter task description e.g. I need someone to help me carry a couch..."
                      style={{ borderRadius: 14 }}
                    />
                  </Form.Item>
                  <Upload
                    className="olitask-photo-row-upload"
                    accept="image/*"
                    beforeUpload={() => false}
                    fileList={photoList}
                    listType="picture-card"
                    maxCount={4}
                    multiple
                    onChange={({ fileList }) => setPhotoList(fileList)}
                  >
                    {photoList.length >= 4 ? null : (
                      <span className="olitask-photo-add">
                        <CameraOutlined />
                        <span>Add Photo</span>
                      </span>
                    )}
                  </Upload>
                  <Text className="olitask-photo-hint">Add up to 4 clear photos.</Text>
                </SectionCard>
              ) : null}

              {requestType !== "TRAVO" ? (
                <SectionCard title={<RequiredLabel>Select Task Location</RequiredLabel>}>
                  <div
                    className="olitask-toggle-group"
                  >
                    <Button
                      className={`olitask-toggle-btn${locationMode === "CURRENT" ? " is-active" : ""}`}
                      loading={locationLoading && locationMode === "CURRENT"}
                      onClick={setCurrentLocation}
                    >
                      Use Current Location
                    </Button>
                    <Button
                      className={`olitask-toggle-btn${locationMode === "MANUAL" ? " is-active" : ""}`}
                      onClick={() => {
                        setLocationMode("MANUAL");
                        if (locationInput === "Current Location") {
                          setLocationInput("");
                          form.setFieldsValue({ location: "" });
                          setCoords(null);
                        }
                      }}
                    >
                      Enter Location
                    </Button>
                  </div>

                  <Form.Item name="location" hidden>
                    <Input />
                  </Form.Item>
                  {!googleMapsApiKey && appConfigGoogleMapsApiKeyLoading ? (
                    <Input disabled placeholder="Loading location search..." />
                  ) : (
                    <GooglePlacesAutocompleteInput
                      googleMapsApiKey={googleMapsApiKey}
                      placeholder="Search city, ZIP, or address"
                      value={locationInput}
                      onChange={(value) => {
                        setLocationInput(value);
                        form.setFieldsValue({ location: value });
                        setLocationMode("MANUAL");
                        setCoords(null);
                      }}
                      types={["geocode"]}
                      extraOptions={[{ value: "Current Location", label: "Use Current Location" }]}
                      onResolvePlace={handleGoogleAddressSelect}
                    />
                  )}
                </SectionCard>
              ) : null}

              {requestType === "TRAVO" ? (
                <SectionCard
                  className="olitask-travo-card"
                  title="🧳 Provide Bags Details"
                  subtitle={travoBagsSummary}
                >
                  <Stepper label="Small" value={bags.smallBags} onChange={(value) => setBags((current) => ({ ...current, smallBags: value }))} />
                  <Stepper label="Medium" value={bags.mediumBags} onChange={(value) => setBags((current) => ({ ...current, mediumBags: value }))} />
                  <Stepper label="Large" value={bags.largeBags} onChange={(value) => setBags((current) => ({ ...current, largeBags: value }))} />
                  <Space size={[8, 8]} wrap className="olitask-travo-flags">
                    <Chip selected={fragile} onClick={() => setFragile((value) => !value)}>
                      ⚠️ Fragile
                    </Chip>
                    <Chip selected={heavy} onClick={() => setHeavy((value) => !value)}>
                      Heavy
                    </Chip>
                  </Space>
                  <div className="olitask-travo-total-row">
                    <Text>Total bags</Text>
                    <strong>{totalBags}</strong>
                  </div>
                </SectionCard>
              ) : null}

              {requestType === "TRAVO" ? (
                <SectionCard
                  className="olitask-travo-card"
                  title="📍 Meeting point"
                  subtitle={travoMeetingSummary}
                >
                  <Form.Item name="meetingPointId" hidden><Input /></Form.Item>
                  <Form.Item name="meetingPoint" hidden><Input /></Form.Item>
                  <Form.Item label={<RequiredLabel>Meeting point</RequiredLabel>}>
                    <Text className="olitask-travo-help">Choose where the helper should meet you.</Text>
                    {meetingPointsError ? (
                      <Alert showIcon type="warning" message="Unable to load meeting points" description={meetingPointsError.message} style={{ marginBottom: 8 }} />
                    ) : null}
                    <Space size={[8, 8]} wrap>
                      <Chip
                        selected={watchedValues.meetingPointId === "CURRENT_LOCATION"}
                        onClick={() => {
                          setCurrentLocation();
                          form.setFieldsValue({
                            meetingPointId: "CURRENT_LOCATION",
                            meetingPoint: "CURRENT_LOCATION",
                          });
                        }}
                      >
                        📍 My Current Location
                      </Chip>
                      {meetingPointsLoading ? (
                        <Text type="secondary">Loading meeting points...</Text>
                      ) : (
                        filteredMeetingPoints.map((meetingPoint) => {
                          const key = travoMeetingPointKey(meetingPoint);
                          return (
                            <Chip
                              key={meetingPoint.id}
                              selected={watchedValues.meetingPointId === meetingPoint.id}
                              onClick={() => {
                                form.setFieldsValue({
                                  meetingPointId: meetingPoint.id,
                                  meetingPoint: key || meetingPoint.id,
                                });
                              }}
                            >
                              {meetingPoint.name}
                            </Chip>
                          );
                        })
                      )}
                    </Space>
                  </Form.Item>
                  {watchedValues.meetingPointId === "CURRENT_LOCATION" ? (
                    <div className="olitask-travo-total-row">
                      <Text>Current Location</Text>
                      <strong>{locationInput || "Not yet detected"}</strong>
                    </div>
                  ) : null}
                  {currentMeetingPointKey === "TERMINAL" ? (
                    <Form.Item name="terminal" label={<RequiredLabel>Terminal</RequiredLabel>}>
                      <Input placeholder="Terminal 3" style={{ borderRadius: 14 }} />
                    </Form.Item>
                  ) : null}
                  {currentMeetingPointKey === "GATE" ? (
                    <Form.Item name="gate" label={<RequiredLabel>Gate</RequiredLabel>}>
                      <Input placeholder="B12" style={{ borderRadius: 14 }} />
                    </Form.Item>
                  ) : null}
                  {currentMeetingPointKey === "OTHER" ? (
                    <Form.Item name="meetingOtherDetail" label={<RequiredLabel>Specify where to meet you</RequiredLabel>}>
                      <Input placeholder="Example: Gate A12 near Starbucks / Carousel 7 / Door 3..." style={{ borderRadius: 14 }} />
                    </Form.Item>
                  ) : null}
                  <Form.Item name="meetingDetail" label="📌 Details (optional)">
                    <Input placeholder="Belt 5, Level 4, Door 2..." style={{ borderRadius: 14 }} />
                  </Form.Item>
                  <Form.Item name="meetingNotes" label="🗺️ Notes (optional)">
                    <Input placeholder="Near Starbucks, by the escalator..." style={{ borderRadius: 14 }} />
                  </Form.Item>
                </SectionCard>
              ) : null}

              {requestType === "TRAVO" ? (
                <SectionCard
                  className="olitask-travo-card"
                  title="⏰ Schedule"
                  subtitle={travoScheduleSummary}
                >
                  <Form.Item name="scheduledAt" label={<RequiredLabel>When</RequiredLabel>}>
                    <DatePicker
                      showTime={{ format: "h:mm A" }}
                      format={TASK_DATE_TIME_FORMAT}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Text className="olitask-travo-when-preview">
                    Selected: {travoScheduleSummary}
                  </Text>
                  <Form.Item name="flightNumber" label="✈️ Flight # (optional)">
                    <Input placeholder="DL 123" style={{ borderRadius: 14 }} />
                  </Form.Item>
                </SectionCard>
              ) : null}

              {requestType === "TRAVO" ? (
                <SectionCard
                  className="olitask-travo-card"
                  title="🌐 Language & extras"
                  subtitle={travoLanguageSummary}
                >
                  <Form.Item name="language" label={<RequiredLabel>Appreciated Assistance</RequiredLabel>}>
                    <Radio.Group>
                      <Space size={[8, 8]} wrap>
                        {POPULAR_LANGUAGES.slice(0, 8).map((language) => (
                          <Radio.Button key={language} value={language}>
                            {language}
                          </Radio.Button>
                        ))}
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                  <div className="olitask-toggle-group">
                    <Button
                      className={`olitask-toggle-btn${watchedValues.escortToGate ? " is-active" : ""}`}
                      onClick={() => form.setFieldsValue({ escortToGate: !watchedValues.escortToGate })}
                    >
                      Escort to gate
                    </Button>
                    <Button
                      className={`olitask-toggle-btn${watchedValues.wheelchair ? " is-active" : ""}`}
                      onClick={() => form.setFieldsValue({ wheelchair: !watchedValues.wheelchair })}
                    >
                      Wheelchair help
                    </Button>
                  </div>
                </SectionCard>
              ) : null}

              {requestType === "TRAVO" ? (
                <SectionCard
                  className="olitask-travo-card"
                  title="📝 Provide More Task Details"
                  subtitle={travoDescriptionSummary}
                >
                  <Form.Item name="description" style={{ marginBottom: 8 }}>
                    <Input.TextArea
                      rows={4}
                      placeholder="Enter details e.g. I need help carrying 3 bags from baggage claim to parking..."
                      style={{ borderRadius: 14 }}
                    />
                  </Form.Item>
                  <Upload
                    className="olitask-photo-row-upload"
                    accept="image/*"
                    beforeUpload={() => false}
                    fileList={photoList}
                    listType="picture-card"
                    maxCount={4}
                    multiple
                    onChange={({ fileList }) => setPhotoList(fileList)}
                  >
                    {photoList.length >= 4 ? null : (
                      <span className="olitask-photo-add">
                        <CameraOutlined />
                        <span>Add Photo</span>
                      </span>
                    )}
                  </Upload>
                  <Text className="olitask-photo-hint">Add up to 4 clear photos.</Text>
                </SectionCard>
              ) : null}

              <SectionCard
                className={requestType === "TRAVO" ? "olitask-travo-card" : ""}
                title={requestType === "TRAVO" ? "💳 Amount Offered & fees" : requestType === "CARE" ? "Schedule & Pay" : "Duration & Token Amount"}
                subtitle={requestType === "TRAVO" ? travoFeesSummary : ""}
              >
                {requestType === "CARE" ? (
                  <>
                    <Form.Item name="scheduleMode" label="Schedule">
                      <Radio.Group buttonStyle="solid">
                        {SCHEDULE_OPTIONS.map(([value, label]) => (
                          <Radio.Button key={value} value={value}>
                            {label}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                    {watchedValues.scheduleMode === "SCHEDULED" ? (
                      <Row gutter={[12, 0]}>
                        <Col xs={24} md={12}>
                          <Form.Item name="startAt" label="Start date & time">
                            <DatePicker
                              showTime={{ format: "h:mm A" }}
                              format={TASK_DATE_TIME_FORMAT}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="endAt" label="End date & time">
                            <DatePicker
                              showTime={{ format: "h:mm A" }}
                              format={TASK_DATE_TIME_FORMAT}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : null}
                    {watchedValues.scheduleMode !== "SCHEDULED" ? (
                      <Row gutter={[12, 0]}>
                        <Col xs={12} md={6}>
                          <Form.Item name="durationHours" label={<RequiredLabel>Duration</RequiredLabel>}>
                            <InputNumber addonAfter="hr" min={0} precision={0} placeholder="0" style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Item name="durationMinutes" label=" ">
                            <InputNumber addonAfter="min" min={0} max={59} precision={0} placeholder="0" style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : null}
                    <Row gutter={[12, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item name="payType" label="Pay Type">
                          <Radio.Group buttonStyle="solid">
                            {PAY_OPTIONS.map(([value, label]) => (
                              <Radio.Button key={value} value={value}>
                                {label}
                              </Radio.Button>
                            ))}
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="carePayAmount" label={watchedValues.payType === "LUMP_SUM" ? "Lump Sum Amount" : "Hourly Rate"}>
                          <InputNumber addonBefore="USD" min={0} precision={2} placeholder="0.00" style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <>
                    <Row gutter={[12, 0]}>
                      <Col xs={12} md={8}>
                        <Form.Item name="durationHours" label={<RequiredLabel>Duration</RequiredLabel>}>
                          <InputNumber addonAfter="hr" min={0} precision={0} placeholder="0" style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={8}>
                        <Form.Item name="durationMinutes" label=" ">
                          <InputNumber addonAfter="min" min={0} max={59} precision={0} placeholder="0" style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="tokenAmount" label={<RequiredLabel>Token Amount</RequiredLabel>}>
                          <InputNumber addonAfter="USD" min={0} precision={2} placeholder={requestType === "TRAVO" ? "Enter Amount" : "Enter Token Amount"} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    {durationMinutes > 0 ? (
                      <Paragraph className="olitask-recommendation-copy">
                        We recommend offering more than <strong>{money(suggestedAmount)}</strong> for this duration to include transport expenses.
                      </Paragraph>
                    ) : null}
                    {requestType === "TRAVO" && tokenAmount > 0 ? (
                      <div className="olitask-travo-charge-summary">
                        <div>
                          <Text type="secondary">Token offered</Text>
                          <strong>{money(tokenAmount)}</strong>
                        </div>
                        <div>
                          <Text type="secondary">Estimated fees</Text>
                          <strong>{money(estimatedFees)}</strong>
                        </div>
                        <div>
                          <Text type="secondary">You pay</Text>
                          <strong>{money(tokenAmount + estimatedFees)}</strong>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </SectionCard>

              {tokenAmount > 0 && requestType !== "TRAVO" ? (
                <SectionCard
                  title="Costs"
                >
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Token offered</Text>
                      <Title level={4} style={{ margin: 0 }}>
                        {money(tokenAmount)}
                      </Title>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Estimated fees</Text>
                      <Title level={4} style={{ margin: 0 }}>
                        {money(estimatedFees)}
                      </Title>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text strong>You pay</Text>
                      <Title level={4} className="olitask-total-title">
                        {money(tokenAmount + estimatedFees)}
                      </Title>
                    </Col>
                  </Row>
                </SectionCard>
              ) : null}

              <Card
                className={`olitask-submit-card${requestType === "TRAVO" ? " is-travo" : ""}`}
                bodyStyle={{ padding: 12 }}
              >
                {requestType === "TRAVO" ? (
                  <Button
                    block
                    className="olitask-submit-btn"
                    htmlType="submit"
                    loading={submitting || uploadingFile}
                    disabled={isHelperContext}
                    type="primary"
                  >
                    {submitting || uploadingFile ? "Submitting..." : "Submit Airport Luggage Help Request"}
                  </Button>
                ) : (
                  <Space align="center" style={{ justifyContent: "space-between", width: "100%" }} wrap>
                    <Text strong className="olitask-themed-strong">
                      Submit this help request to Olitask.
                    </Text>
                    <Space wrap>
                      <Button onClick={() => navigate(paths.myTasks)}>Cancel</Button>
                      <Button
                        className="olitask-submit-btn"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={submitting || uploadingFile}
                        disabled={isHelperContext}
                        type="primary"
                      >
                        {submitting || uploadingFile ? "Submitting..." : "Submit Help Request"}
                      </Button>
                    </Space>
                  </Space>
                )}
              </Card>
            </Space>
          </Form>
        </Space>
      </div>
    </div>
  );
}
